// src/helpers/word.ts

import { streamErrorToPostHog, ApiNames, ErrorCodes } from "../api/utils/captureError";
import { SUPPORT_EMAIL } from "../config/constants";

/**
 * Get the URL of the currently open document (e.g., SharePoint URL)
 * Returns null for local documents or if URL is unavailable
 */
export function getDocumentUrl(): string | null {
  try {
    const rawUrl = Office.context?.document?.url || null;
    if (!rawUrl) return null;

    // Transform to SharePoint web viewer URL if it's a SharePoint document
    return transformToSharePointWebUrl(rawUrl);
  } catch {
    return null;
  }
}

/**
 * Transform a direct SharePoint file URL to a browser-viewable URL
 * Input:  https://tenant.sharepoint.com/sites/Site/Shared Documents/file.docx
 * Output: https://tenant.sharepoint.com/:w:/r/sites/Site/Shared Documents/file.docx?web=1
 *
 * URL format: /:w:/r/ for Word, /:x:/r/ for Excel, /:p:/r/ for PowerPoint
 * /r = read-only view with existing permissions
 * ?web=1 = force open in browser
 */
function transformToSharePointWebUrl(directUrl: string): string {
  try {
    const url = new URL(directUrl);

    // Only transform SharePoint URLs
    if (!url.hostname.includes("sharepoint.com")) {
      return directUrl;
    }

    // Determine file type prefix based on extension
    const pathname = url.pathname.toLowerCase();
    let typePrefix = ":w:"; // Default to Word

    if (pathname.endsWith(".xlsx") || pathname.endsWith(".xls")) {
      typePrefix = ":x:";
    } else if (pathname.endsWith(".pptx") || pathname.endsWith(".ppt")) {
      typePrefix = ":p:";
    }

    // Build browser-viewable URL: origin + /:w:/r + pathname + ?web=1
    const webViewerUrl = `${url.origin}/${typePrefix}/r${url.pathname}?web=1`;
    return webViewerUrl;
  } catch {
    // If URL parsing fails, return original
    return directUrl;
  }
}

export async function getEntireDocument(): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      // Use Word.run to access the Word-specific APIs
      Word.run(async (context) => {
        // Get the entire document body
        const body = context.document.body;

        // Get the OOXML representation
        const ooxml = body.getOoxml();

        await context.sync();

        resolve(ooxml.value);
      }).catch((error) => {
        console.error("Error in Word.run:", error);
        streamErrorToPostHog(error, ApiNames.WORD_API, ErrorCodes.DOCUMENT_READ_ERROR);
        reject(
          new Error(
            `There was an error reading the document, please click on \"Retry\". If the issue persists, contact ${SUPPORT_EMAIL}`
          )
        );
      });
    } catch (e: any) {
      console.error("Exception in getEntireDocument:", e);
      streamErrorToPostHog(e, ApiNames.WORD_API, ErrorCodes.DOCUMENT_READ_ERROR);
      reject(
        new Error(
          `There was an unexpected error reading the document, please click on \"Retry\". If the issue persists, contact ${SUPPORT_EMAIL}`
        )
      );
    }
  });
}

/**
 * Get document content along with metadata (track changes status, etc.)
 * This optimized function combines multiple Word API calls into one for efficiency
 *
 * @returns Object containing document OOXML and metadata
 */
export async function getDocumentWithMetadata(): Promise<{
  ooxml: string;
  trackChangesMode: string;
  markupMode: string;
}> {
  // Create a timeout promise that rejects after 2 seconds
  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error("Word API timeout after 5 seconds")), 5000)
  );

  // Check if WordApi 1.4 is supported (required for changeTrackingMode)
  const supportsWordApi14 =
    Office.context.requirements?.isSetSupported?.("WordApi", "1.4") ?? false;

  // activeWindow.view.markupMode needs WordApiDesktop 1.4 (Word on Windows from
  // Version 2508, Mac from 16.100.4). Absent on Word on the web, on iPad and on
  // volume-licensed builds, which is fine: Word on the web has no balloons
  // setting to report.
  const supportsMarkupMode =
    Office.context.requirements?.isSetSupported?.("WordApiDesktop", "1.4") ?? false;

  // Create the main operation promise
  const operationPromise = new Promise<{
    ooxml: string;
    trackChangesMode: string;
    markupMode: string;
  }>((resolve, reject) => {
    try {
      Word.run(async (context) => {
        // Get the document body
        const body = context.document.body;
        const ooxml = body.getOoxml();

        // Load track changes mode if API is supported
        let trackChangesMode: string = "unknown";
        // How Word is DISPLAYING those tracked changes, which decides whether
        // tracked-deleted text is in the text layer the add-in later searches.
        let markupMode: string = "unknown";

        if (supportsWordApi14) {
          try {
            const document = context.document;
            document.load("changeTrackingMode");
          } catch (loadError) {
            console.warn("Failed to load changeTrackingMode property:", loadError);
            // Continue without track changes - will return "unknown"
          }
        } else {
          console.info("WordApi 1.4 not supported, skipping track changes detection");
        }

        // Sync for the values a redline cannot be generated without. The display
        // mode is read separately below, so it can never cost us these.
        await context.sync();

        // Extract track changes mode if it was loaded
        if (supportsWordApi14) {
          try {
            const mode = context.document.changeTrackingMode;
            const modeString = String(mode);

            if (modeString === "Off" || mode === Word.ChangeTrackingMode.off) {
              trackChangesMode = "off";
            } else if (modeString === "TrackAll" || mode === Word.ChangeTrackingMode.trackAll) {
              trackChangesMode = "trackAll";
            } else if (
              modeString === "TrackMineOnly" ||
              mode === Word.ChangeTrackingMode.trackMineOnly
            ) {
              trackChangesMode = "trackMineOnly";
            }
          } catch (modeError) {
            console.warn("Failed to read changeTrackingMode value:", modeError);
            // trackChangesMode remains "unknown"
          }
        }

        // Read the display mode in its OWN sync, inside a try/catch.
        //
        // isSetSupported is not a guarantee the property resolves: office.js is
        // loaded from the CDN and is always current, so on a build that predates
        // the View API the proxy and the load() both succeed locally and only
        // the sync rejects. Batching this into the sync above would therefore
        // let an optional value take down the OOXML and the tracking mode with
        // it, and losing the tracking mode is not a degradation we can absorb -
        // the backend would default to trackAll and the add-in would switch
        // Track Changes on for a user who had it off. The cost is one extra
        // round trip on hosts that report the requirement set.
        if (supportsMarkupMode) {
          try {
            const view = context.document.activeWindow.view;
            view.load("markupMode");
            await context.sync();
            // "Balloon" | "Inline" | "Mixed". Only "Balloon" hides deletions.
            markupMode = String(view.markupMode);
          } catch (viewError) {
            console.warn("Failed to read markupMode, continuing without it:", viewError);
            // markupMode remains "unknown", which the backend reads as
            // "keep the previous deletions-inline behaviour".
          }
        }

        resolve({
          ooxml: ooxml.value,
          trackChangesMode,
          markupMode,
        });
      }).catch((error) => {
        console.error("Error in Word.run:", error);
        streamErrorToPostHog(error, ApiNames.WORD_API, ErrorCodes.DOCUMENT_READ_ERROR);
        reject(
          new Error(
            `There was an error reading the document & its metadata, please click on \"Retry\". If the issue persists, contact ${SUPPORT_EMAIL}`
          )
        );
      });
    } catch (e: any) {
      console.error("Exception in getDocumentWithMetadata:", e);
      streamErrorToPostHog(e, ApiNames.WORD_API, ErrorCodes.DOCUMENT_READ_ERROR);
      reject(
        new Error(
          `There was an unexpected error reading the document & its metadata, please click on \"Retry\". If the issue persists, contact ${SUPPORT_EMAIL}`
        )
      );
    }
  });

  // Race between operation and timeout
  try {
    return await Promise.race([operationPromise, timeoutPromise]);
  } catch (error: any) {
    // If timeout or other error, fall back to basic document retrieval
    console.error("getDocumentWithMetadata failed, falling back:", error);

    // Fallback: try to get just the document without track changes
    try {
      const ooxml = await getEntireDocument();
      return { ooxml, trackChangesMode: "unknown", markupMode: "unknown" };
    } catch (fallbackError: any) {
      throw new Error(`Document retrieval failed: ${fallbackError.message}`);
    }
  }
}

/**
 * Normalize a paragraph text snapshot for stale-paragraph comparison.
 *
 * Collapses any run of whitespace (\r, \n, \t, NBSP, and other Unicode
 * whitespace covered by \s) into a single space and trims the ends. Applied
 * to both sides of the compare so that artifacts like trailing carriage
 * returns from Word's `paragraph.text` API or the explicit " <br>" spacing
 * the backend introduces during preprocessing do not cause false positives.
 *
 * Punctuation, casing, and any non-whitespace character are left alone —
 * those are real content differences.
 *
 * Important distinction about whitespace edits:
 *
 *   Type 1 — modifying an EXISTING whitespace run. e.g. a single space
 *   between two words becomes two spaces, or a tab becomes a space.
 *   Both sides collapse to the same single space after this function, so
 *   the stale check returns "not stale" and the apply runs. That is safe
 *   because the autoredline apply path uses Word's body.search with
 *   `ignorePunct: true, ignoreSpace: true` (see wordDocumentSearch.ts),
 *   which treats whitespace runs equivalently when locating searchPhrases.
 *   Blocking the apply for this kind of change would be friction with no
 *   correctness benefit.
 *
 *   Type 2 — INSERTING whitespace where there was none, or DELETING
 *   whitespace that existed between two non-whitespace characters.
 *   e.g. `covenants,` becoming `covenants ,` (space inserted between a
 *   word and the comma), or `covenants ,` becoming `covenants,` (space
 *   removed). Neither normalizes away — both sides remain different
 *   strings after collapsing. The stale check correctly flags these and
 *   blocks the apply. Behaviour by design: changing whether whitespace
 *   is present at a position is a structural shift in the paragraph that
 *   could legitimately misplace edits, so the conservative "block and ask
 *   Contract Space to redo" outcome is the right one.
 */
export function normalizeParagraphText(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

export type TrackChangesMode = "off" | "trackAll" | "trackMineOnly";

export function normalizeTrackChangesMode(
  mode: string | undefined | null
): TrackChangesMode | "unknown" {
  if (!mode) return "unknown";
  const lower = mode.toLowerCase();
  if (lower === "off") return "off";
  if (lower === "trackall") return "trackAll";
  if (lower === "trackmineonly") return "trackMineOnly";
  return "unknown";
}

/**
 * Read the document's current Track Changes mode.
 * Returns "unknown" when WordApi 1.4 is unsupported or the read fails — callers
 * should treat "unknown" as "can't compare; skip any state-dependent logic".
 */
export async function getCurrentTrackChangesMode(): Promise<TrackChangesMode | "unknown"> {
  const supportsWordApi14 =
    Office.context.requirements?.isSetSupported?.("WordApi", "1.4") ?? false;
  if (!supportsWordApi14) return "unknown";

  try {
    return await Word.run(async (context) => {
      const document = context.document;
      document.load("changeTrackingMode");
      await context.sync();

      const mode = document.changeTrackingMode;
      const modeString = String(mode);
      if (modeString === "Off" || mode === Word.ChangeTrackingMode.off) return "off";
      if (modeString === "TrackAll" || mode === Word.ChangeTrackingMode.trackAll) return "trackAll";
      if (modeString === "TrackMineOnly" || mode === Word.ChangeTrackingMode.trackMineOnly)
        return "trackMineOnly";
      return "unknown";
    });
  } catch (err: any) {
    console.error("Failed to read changeTrackingMode:", err);
    streamErrorToPostHog(err, ApiNames.WORD_API, ErrorCodes.DOCUMENT_READ_ERROR);
    return "unknown";
  }
}

/**
 * Read how Word is currently DISPLAYING tracked changes.
 *
 * Separate from Track Changes itself: this is the Review > Show Markup >
 * Balloons setting, and it decides whether tracked deletions are present in the
 * text layer that search() and paragraph.text read. Returns "Balloon" |
 * "Inline" | "Mixed", or "unknown" when the host cannot report it - which
 * callers must treat as "can't compare, skip any mode-dependent logic" rather
 * than as a value that could differ.
 */
export async function getMarkupDisplayMode(): Promise<string> {
  const supportsMarkupMode =
    Office.context.requirements?.isSetSupported?.("WordApiDesktop", "1.4") ?? false;
  if (!supportsMarkupMode) return "unknown";

  try {
    return await Word.run(async (context) => {
      const view = context.document.activeWindow.view;
      view.load("markupMode");
      await context.sync();
      return String(view.markupMode);
    });
  } catch (err: any) {
    // Not reported to PostHog: on hosts that advertise the requirement set but
    // cannot serve the property this would fire on every apply, and the caller
    // degrades cleanly to skipping the comparison.
    console.warn("Failed to read markupMode:", err);
    return "unknown";
  }
}

export interface SetTrackChangesModeResult {
  ok: boolean;
  unsupported: boolean;
  error?: string;
}

/**
 * Set the document's Track Changes mode to a specific value.
 * No-op when the document is already in that mode.
 * Gracefully handles unsupported platforms (returns unsupported=true).
 */
export async function setTrackChangesMode(
  target: TrackChangesMode
): Promise<SetTrackChangesModeResult> {
  const supportsWordApi14 =
    Office.context.requirements?.isSetSupported?.("WordApi", "1.4") ?? false;
  if (!supportsWordApi14) {
    return { ok: false, unsupported: true };
  }

  const targetEnum =
    target === "off"
      ? Word.ChangeTrackingMode.off
      : target === "trackAll"
        ? Word.ChangeTrackingMode.trackAll
        : Word.ChangeTrackingMode.trackMineOnly;
  const targetString =
    target === "off" ? "Off" : target === "trackAll" ? "TrackAll" : "TrackMineOnly";

  try {
    return await Word.run(async (context) => {
      const document = context.document;
      document.load("changeTrackingMode");
      await context.sync();

      // Defensive OR: matches the same pattern getCurrentTrackChangesMode uses
      // to handle hosts where the runtime value of changeTrackingMode is the
      // string form vs. the enum reference.
      const currentRaw = document.changeTrackingMode;
      if (String(currentRaw) === targetString || currentRaw === targetEnum) {
        return { ok: true, unsupported: false };
      }

      document.changeTrackingMode = targetEnum;
      await context.sync();
      return { ok: true, unsupported: false };
    });
  } catch (err: any) {
    console.error("Failed to set changeTrackingMode:", err);
    streamErrorToPostHog(err, ApiNames.WORD_API, ErrorCodes.DOCUMENT_READ_ERROR);
    return {
      ok: false,
      unsupported: false,
      error: err?.message || "Unknown error setting Track Changes mode",
    };
  }
}

export interface EnableTrackChangesResult {
  enabled: boolean;
  alreadyEnabled: boolean;
  unsupported: boolean;
  error?: string;
}

/**
 * Enable Track Changes (TrackAll mode) on the active document.
 * Returns immediately if already enabled. Gracefully handles unsupported platforms.
 */
export async function enableTrackChanges(): Promise<EnableTrackChangesResult> {
  const supportsWordApi14 =
    Office.context.requirements?.isSetSupported?.("WordApi", "1.4") ?? false;

  if (!supportsWordApi14) {
    return { enabled: false, alreadyEnabled: false, unsupported: true };
  }

  try {
    return await Word.run(async (context) => {
      const document = context.document;
      document.load("changeTrackingMode");
      await context.sync();

      const currentMode = document.changeTrackingMode;
      if (currentMode === Word.ChangeTrackingMode.trackAll) {
        return { enabled: false, alreadyEnabled: true, unsupported: false };
      }

      document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      await context.sync();

      return { enabled: true, alreadyEnabled: false, unsupported: false };
    });
  } catch (err: any) {
    console.error("Failed to enable Track Changes:", err);
    streamErrorToPostHog(err, ApiNames.WORD_API, ErrorCodes.DOCUMENT_READ_ERROR);
    return {
      enabled: false,
      alreadyEnabled: false,
      unsupported: false,
      error: err?.message || "Unknown error enabling Track Changes",
    };
  }
}

export async function getSelection(): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      Office.context.document.getSelectedDataAsync(
        Office.CoercionType.Ooxml, // Changed from Text to Ooxml
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const selectedOoxml = result.value as string;
            resolve(selectedOoxml);
          } else {
            console.error("Error getting OOXML selection:", result.error);
            streamErrorToPostHog(result.error, ApiNames.WORD_API, ErrorCodes.GET_SELECTION_ERROR);
            reject(
              new Error(
                `There was an error reading the selection, please click on \"Retry\". If the issue persists, contact ${SUPPORT_EMAIL}`
              )
            );
          }
        }
      );
    } catch (e) {
      console.error("Exception in getSelection:", e);
      streamErrorToPostHog(e, ApiNames.WORD_API, ErrorCodes.GET_SELECTION_ERROR);
      reject(
        new Error(
          `There was an unexpected error reading the selection, please click on \"Retry\". If the issue persists, contact ${SUPPORT_EMAIL}`
        )
      );
    }
  });
}

/**
 * Get selected text as plain text for UI display only
 * This is separate from getSelection() and doesn't interfere with existing OOXML logic
 */
export async function getSelectedText(): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      Office.context.document.getSelectedDataAsync(
        Office.CoercionType.Text,
        { valueFormat: Office.ValueFormat.Unformatted },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const selectedText = result.value as string;
            resolve(selectedText.trim());
          } else {
            console.error("Error getting plain text selection:", result.error);
            resolve(""); // Return empty string instead of rejecting
          }
        }
      );
    } catch (e) {
      console.error("Exception in getSelectedText:", e);
      streamErrorToPostHog(e, ApiNames.WORD_API, ErrorCodes.GET_SELECTED_TEXT_ERROR);
      resolve(""); // Return empty string instead of rejecting
    }
  });
}

export function insertText(text: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      Office.context.document.setSelectedDataAsync(
        text,
        { coercionType: Office.CoercionType.Text },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            console.error("Error inserting text:", result.error);
            streamErrorToPostHog(result.error, ApiNames.WORD_API, ErrorCodes.INSERT_TEXT_ERROR);
            reject(new Error(result.error?.message || "Failed to insert text"));
          }
        }
      );
    } catch (e) {
      console.error("Exception in insertText:", e);
      streamErrorToPostHog(e, ApiNames.WORD_API, ErrorCodes.INSERT_TEXT_ERROR);
      reject(e);
    }
  });
}

export async function insertTextWithFormatting(text: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      Word.run(async (context) => {
        // Get the current selection
        const selection = context.document.getSelection();

        // Load only the formatting properties we care about
        selection.font.load([
          "bold",
          "italic",
          "underline",
          "color",
          "size",
          "name",
          "highlightColor",
        ]);
        selection.paragraphs.load(["alignment"]);
        await context.sync();

        // Store font attributes
        const fontAttributes = {
          name: selection.font.name,
          size: selection.font.size,
          bold: selection.font.bold,
          italic: selection.font.italic,
          underline: selection.font.underline,
          color: selection.font.color,
          highlightColor: selection.font.highlightColor,
        };

        // Store paragraph alignment
        const paragraphAlignment =
          selection.paragraphs.items.length > 0 ? selection.paragraphs.items[0].alignment : null;

        // Insert the new text
        const newRange = selection.insertText(text, Word.InsertLocation.replace);

        // Apply font name and size first
        if (fontAttributes.name) newRange.font.name = fontAttributes.name;
        if (fontAttributes.size) newRange.font.size = fontAttributes.size;

        // Apply paragraph alignment
        newRange.paragraphs.load("items");
        await context.sync();

        if (paragraphAlignment && newRange.paragraphs.items.length > 0) {
          newRange.paragraphs.items[0].alignment = paragraphAlignment;
        }

        // Apply other font attributes
        if (fontAttributes.bold !== undefined) newRange.font.bold = fontAttributes.bold;
        if (fontAttributes.italic !== undefined) newRange.font.italic = fontAttributes.italic;
        if (fontAttributes.underline !== undefined)
          newRange.font.underline = fontAttributes.underline;
        if (fontAttributes.color) newRange.font.color = fontAttributes.color;
        if (fontAttributes.highlightColor)
          newRange.font.highlightColor = fontAttributes.highlightColor;

        await context.sync();
        resolve();
      }).catch((error) => {
        console.error("Error in Word.run when inserting formatted text:", error);
        streamErrorToPostHog(
          error,
          ApiNames.WORD_API,
          ErrorCodes.INSERT_TEXT_WITH_FORMATTING_ERROR
        );
        reject(new Error(`Failed to insert text with formatting: ${error.message}`));
      });
    } catch (e: any) {
      console.error("Exception in insertTextWithFormatting:", e);
      streamErrorToPostHog(e, ApiNames.WORD_API, ErrorCodes.INSERT_TEXT_WITH_FORMATTING_ERROR);
      reject(e);
    }
  });
}

/**
 * Get the binary content of the currently open document as an ArrayBuffer.
 * Uses Office.context.document.getFileAsync with compressed format to get a valid .docx file.
 * The file is read in slices and concatenated into a single ArrayBuffer.
 */
export async function getDocumentFileBytes(): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    try {
      Office.context.document.getFileAsync(
        Office.FileType.Compressed,
        { sliceSize: 4194304 }, // 4 MB slices
        (fileResult) => {
          if (fileResult.status !== Office.AsyncResultStatus.Succeeded) {
            const error = new Error(fileResult.error?.message || "Failed to read document file");
            streamErrorToPostHog(error, ApiNames.WORD_API, ErrorCodes.GET_FILE_BYTES_ERROR);
            reject(error);
            return;
          }

          const file = fileResult.value;
          const sliceCount = file.sliceCount;

          if (sliceCount === 0) {
            file.closeAsync();
            reject(new Error("Document file has no content"));
            return;
          }

          const slices: Uint8Array[] = [];
          let slicesRead = 0;

          function readSlice(index: number) {
            file.getSliceAsync(index, (sliceResult) => {
              if (sliceResult.status !== Office.AsyncResultStatus.Succeeded) {
                file.closeAsync();
                const error = new Error(
                  sliceResult.error?.message || `Failed to read file slice ${index}`
                );
                streamErrorToPostHog(error, ApiNames.WORD_API, ErrorCodes.GET_FILE_BYTES_ERROR);
                reject(error);
                return;
              }

              slices.push(new Uint8Array(sliceResult.value.data));
              slicesRead++;

              if (slicesRead === sliceCount) {
                file.closeAsync();
                // Concatenate all slices into a single ArrayBuffer
                const totalLength = slices.reduce((sum, s) => sum + s.length, 0);
                const combined = new Uint8Array(totalLength);
                let offset = 0;
                for (const slice of slices) {
                  combined.set(slice, offset);
                  offset += slice.length;
                }
                resolve(combined.buffer);
              } else {
                readSlice(index + 1);
              }
            });
          }

          readSlice(0);
        }
      );
    } catch (e) {
      console.error("Exception in getDocumentFileBytes:", e);
      streamErrorToPostHog(e, ApiNames.WORD_API, ErrorCodes.GET_FILE_BYTES_ERROR);
      reject(
        new Error(
          `There was an unexpected error reading the document file. If the issue persists, contact ${SUPPORT_EMAIL}`
        )
      );
    }
  });
}

/**
 * Extract the filename from the currently open document URL.
 * Falls back to "Document.docx" if the URL is unavailable or unparseable.
 */
export function getDocumentName(): string {
  const fallback = "Document.docx";
  try {
    const url = Office.context?.document?.url;
    if (!url) return fallback;

    // Extract filename from path (works for both URLs and local file paths)
    const lastSegment = url.split("/").pop() || url.split("\\").pop();
    if (!lastSegment) return fallback;

    // Remove query string if present
    const name = lastSegment.split("?")[0];
    return name || fallback;
  } catch {
    return fallback;
  }
}

export async function insertTextWithAdvancedFormatting(text: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      Word.run(async (context) => {
        const selection = context.document.getSelection();

        // Load paragraphs first to determine if we have multiple paragraphs
        selection.paragraphs.load("items");
        await context.sync();

        const paragraphCount = selection.paragraphs.items.length;

        // If we have multiple paragraphs, handle each separately
        if (paragraphCount > 1) {
          // Load formatting information for each paragraph
          for (let i = 0; i < paragraphCount; i++) {
            selection.paragraphs.items[i].font.load([
              "name",
              "size",
              "bold",
              "italic",
              "underline",
              "color",
              "highlightColor",
              "subscript",
              "superscript",
              "strikeThrough",
            ]);
            selection.paragraphs.items[i].load("alignment");
          }
          await context.sync();

          // Store formatting details for each paragraph
          const paragraphFormats = [];
          for (let i = 0; i < paragraphCount; i++) {
            const paragraph = selection.paragraphs.items[i];
            const format = {
              alignment: paragraph.alignment,
              font: {
                name: paragraph.font.name,
                size: paragraph.font.size,
                bold: paragraph.font.bold,
                italic: paragraph.font.italic,
                underline: paragraph.font.underline,
                color: paragraph.font.color,
                highlightColor: paragraph.font.highlightColor,
                subscript: paragraph.font.subscript,
                superscript: paragraph.font.superscript,
                strikethrough: paragraph.font.strikeThrough,
              },
            };
            paragraphFormats.push(format);
          }

          // Split the new text into paragraphs
          const textParagraphs = text.split(/\n+/).filter((p) => p.trim() !== "");

          // Clear the entire selection first
          const range = selection.insertText("", Word.InsertLocation.replace);
          await context.sync();

          // Use a simpler approach: insert all paragraphs at once with newlines
          // and then apply formatting to each paragraph separately
          const fullText = textParagraphs.join("\n\n");
          const newRange = range.insertText(fullText, Word.InsertLocation.replace);

          // Load all paragraphs in the new range
          newRange.paragraphs.load("items");
          await context.sync();

          // Apply formatting to each paragraph
          const numParagraphs = Math.min(newRange.paragraphs.items.length, paragraphFormats.length);
          for (let i = 0; i < numParagraphs; i++) {
            const paragraph = newRange.paragraphs.items[i];
            const format = paragraphFormats[i];

            // Apply formatting directly to this paragraph
            if (format.alignment) paragraph.alignment = format.alignment;

            // Load the paragraph's font
            paragraph.font.load();
            await context.sync();

            // Apply font formatting
            if (format.font.name) paragraph.font.name = format.font.name;
            if (format.font.size) paragraph.font.size = format.font.size;
            if (format.font.bold !== null) paragraph.font.bold = format.font.bold;
            if (format.font.italic !== null) paragraph.font.italic = format.font.italic;
            if (format.font.underline !== "Mixed") paragraph.font.underline = format.font.underline;
            if (format.font.color) paragraph.font.color = format.font.color;
            if (format.font.highlightColor)
              paragraph.font.highlightColor = format.font.highlightColor;
            if (format.font.subscript !== null) paragraph.font.subscript = format.font.subscript;
            if (format.font.superscript !== null)
              paragraph.font.superscript = format.font.superscript;
            if (format.font.strikethrough !== null)
              paragraph.font.strikeThrough = format.font.strikethrough;

            await context.sync();
          }
        } else {
          // Single paragraph approach remains the same
          selection.font.load([
            "bold",
            "italic",
            "underline",
            "color",
            "size",
            "name",
            "highlightColor",
            "subscript",
            "superscript",
            "strikethrough",
          ]);
          selection.paragraphs.load(["alignment"]);
          await context.sync();

          // Store only the formatting attributes we care about
          const fontAttributes = {
            name: selection.font.name,
            size: selection.font.size,
            bold: selection.font.bold,
            italic: selection.font.italic,
            underline: selection.font.underline,
            color: selection.font.color,
            highlightColor: selection.font.highlightColor,
            subscript: selection.font.subscript,
            superscript: selection.font.superscript,
            strikethrough: selection.font.strikeThrough,
          };

          // Store paragraph alignment
          const paragraphAlignment =
            selection.paragraphs.items.length > 0 ? selection.paragraphs.items[0].alignment : null;

          // Insert text
          const newRange = selection.insertText(text, Word.InsertLocation.replace);

          // Apply direct formatting attributes in specific order
          // First apply the font family and size as these are most fundamental
          if (fontAttributes.name) newRange.font.name = fontAttributes.name;
          if (fontAttributes.size) newRange.font.size = fontAttributes.size;

          // Apply paragraph alignment if available
          newRange.paragraphs.load("items");
          await context.sync();

          if (paragraphAlignment && newRange.paragraphs.items.length > 0) {
            newRange.paragraphs.items[0].alignment = paragraphAlignment;
          }

          if (fontAttributes.bold !== null) newRange.font.bold = fontAttributes.bold;
          if (fontAttributes.italic !== null) newRange.font.italic = fontAttributes.italic;
          if (fontAttributes.underline !== "Mixed")
            newRange.font.underline = fontAttributes.underline;
          if (fontAttributes.color) newRange.font.color = fontAttributes.color;
          if (fontAttributes.highlightColor)
            newRange.font.highlightColor = fontAttributes.highlightColor;
          if (fontAttributes.subscript !== null) newRange.font.subscript = fontAttributes.subscript;
          if (fontAttributes.superscript !== null)
            newRange.font.superscript = fontAttributes.superscript;
          if (fontAttributes.strikethrough !== null)
            newRange.font.strikeThrough = fontAttributes.strikethrough;
        }

        await context.sync();
        resolve();
      }).catch((error) => {
        console.error("Error in Word.run:", error);
        streamErrorToPostHog(
          error,
          ApiNames.WORD_API,
          ErrorCodes.INSERT_TEXT_WITH_FORMATTING_ERROR
        );
        reject(new Error(`Failed to insert text with formatting: ${error.message}`));
      });
    } catch (e: any) {
      console.error("Exception in insertTextWithAdvancedFormatting:", e);
      streamErrorToPostHog(e, ApiNames.WORD_API, ErrorCodes.INSERT_TEXT_WITH_FORMATTING_ERROR);
      reject(e);
    }
  });
}
